import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { isStudyDirty } from './studyGuard';
import QuitConfirm from '../shared/QuitConfirm';
import {
  RiHome4Line, RiFlashlightLine, RiFileTextLine, RiFileList3Line,
  RiBookOpenLine, RiSettings3Line, RiLogoutBoxLine,
  RiCharacterRecognitionLine, RiUser3Line,
  RiEditLine,
} from 'react-icons/ri';
import { LuPanelLeftOpen, LuPanelRightOpen } from 'react-icons/lu';
import { C } from '../../data/constants';
import { RiCloseLine as RiCloseIcon } from 'react-icons/ri';
import KitsuMark from './../brand/KitsuMark';

const NAV = [
  { to:'/dashboard',  label:'Learn',             icon:RiHome4Line      },
  { to:'/kana',       label:'Hiragana & Katakana',icon:RiCharacterRecognitionLine },
  { to:'/kanji',      label:'Kanji',              icon:RiBookOpenLine   },
  { to:'/flashcards', label:'Flashcards',         icon:RiFlashlightLine },
  { to:'/quiz',       label:'JLPT Quiz',          icon:RiFileTextLine   },
  { to:'/practice-tests', label:'Practice Tests',  icon:RiFileList3Line  },
  { to:'/grammar',    label:'Grammar',            icon:RiEditLine       },
  { to:'/profile',    label:'Profile',            icon:RiUser3Line      },
];

const MOBILE_BREAKPOINT = 1024;

export default function AppSidebar({ onLogout, isOpen, onClose, collapsed, onToggleCollapse }) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  /* Where the user asked to go while an exercise still had unsaved work.
     Held rather than followed, until they confirm. */
  const [pending, setPending] = useState(null);

  const leaveTo = (event, to) => {
    if (isStudyDirty()) {
      event.preventDefault();      // stop the NavLink before it navigates
      setPending({ to });
      return;
    }
    onClose();
  };

  const confirmLeave = () => {
    const target = pending;
    setPending(null);
    if (!target) return;
    /* The guard belongs to the exercise being unmounted; navigating away is
       what unmounts it, so there is nothing to release by hand here. */
    if (target.logout) { onLogout(); navigate('/'); return; }
    onClose();
    navigate(target.to);
  };

  // The collapse feature is desktop-only. On small screens the sidebar is a
  // drawer opened from the mobile header, and it must always render expanded.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= MOBILE_BREAKPOINT;
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const effCollapsed = isMobile ? false : collapsed && !isHovered;
  // Hovering while pinned-collapsed only previews the expanded sidebar as an
  // overlay — it doesn't count as "expanded" for layout/reserved-space purposes.
  const isHoverPreview = !isMobile && collapsed && isHovered;

  return (
    <aside
      className={`app-sidebar ${isOpen ? 'open' : ''} ${effCollapsed ? 'collapsed' : ''} ${isHoverPreview ? 'hover-preview' : ''}`}
      onMouseEnter={() => { if (!isMobile && collapsed) setIsHovered(true); }}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        width: effCollapsed ? 68 : 252,
        flexShrink: 0,
        background: C.surface,
        borderRight: `1px solid ${C.border}`,
        display: 'flex',
        flexDirection: 'column',
        padding: effCollapsed ? '20px 8px' : '22px 16px',
        gap: 4,
        position: 'fixed',
        left: 0,
        top: 0,
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        // Mobile stacking (above the dark drawer scrim) is handled entirely by
        // the stylesheet's z-index:2000 — leave it unset here so it isn't
        // clobbered by this inline style, which wins over non-!important CSS.
        zIndex: isMobile ? undefined : (isHoverPreview ? 60 : 1),
        willChange: 'width, padding, transform',
        transition: 'width 0.18s cubic-bezier(0.22, 1, 0.36, 1), padding 0.18s cubic-bezier(0.22, 1, 0.36, 1), transform 0.18s cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      <button className="sidebar-close-btn" onClick={onClose} aria-label="Close Menu">
        <RiCloseIcon size={20} />
      </button>
      <div className="brand-mark" style={{ minHeight: 62, display: 'flex', alignItems: 'center', justifyContent: effCollapsed ? 'center' : 'flex-start', gap: 10, padding: '12px 8px 20px', whiteSpace: 'nowrap' }}>
        <KitsuMark size={30} listen style={{ flexShrink: 0 }} />
        {!effCollapsed && <span style={{ fontWeight: 900, fontSize: 21, letterSpacing: -0.5, whiteSpace: 'nowrap' }}>Kitsu<span style={{ color: C.accent }}>Speak</span></span>}
      </div>

      <div style={{ minHeight: 26, opacity: effCollapsed ? 0 : 1, fontSize: 12, fontWeight: 900, color: C.faint, padding: '0 8px 8px', letterSpacing: 1.2, textTransform: 'uppercase', transition: 'opacity 0.18s ease' }}>Menu</div>

      {NAV.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          data-tour={`nav-${to.replace('/', '')}`}
          className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
          onClick={e => leaveTo(e, to)}
          title={effCollapsed ? label : undefined}
          style={effCollapsed ? { justifyContent: 'center', padding: '10px 8px', minHeight: 42 } : { minHeight: 42 }}
        >
          <Icon size={18} />
          {!effCollapsed && <span className="nav-item-label">{label}</span>}
        </NavLink>
      ))}

      <div style={{ flex: 1 }} />

      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!isMobile && (
          <button
            className="nav-item"
            type="button"
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{ justifyContent: effCollapsed ? 'center' : 'flex-start', width: '100%', background: 'transparent', minHeight: 42, padding: effCollapsed ? '10px 8px' : '10px 13px' }}
          >
            {collapsed ? <LuPanelLeftOpen size={18} /> : <LuPanelRightOpen size={18} />}
            {!effCollapsed && <span className="nav-item-label">{collapsed ? 'Expand' : 'Collapse'}</span>}
          </button>
        )}
        <NavLink
          to="/settings"
          data-tour="nav-settings"
          className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
          onClick={e => leaveTo(e, '/settings')}
          title={effCollapsed ? 'Account' : undefined}
          style={{ justifyContent: effCollapsed ? 'center' : 'flex-start', minHeight: 42, padding: effCollapsed ? '10px 8px' : '10px 13px' }}
        >
          <RiSettings3Line size={18} />
          {!effCollapsed && <span className="nav-item-label">Account</span>}
        </NavLink>
        <div className="nav-item" data-nav="logout" title={effCollapsed ? 'Logout' : undefined} style={{ color: C.red, justifyContent: effCollapsed ? 'center' : 'flex-start', minHeight: 42, padding: effCollapsed ? '10px 8px' : '10px 13px' }} onClick={() => { if (isStudyDirty()) { setPending({ logout: true }); return; } onLogout(); navigate('/'); }}>
          <RiLogoutBoxLine size={18} />
          {!effCollapsed && <span className="nav-item-label">Logout</span>}
        </div>
      </div>

      {/* Portalled to <body> by QuitConfirm, so the sidebar's own stacking
          context and overflow cannot clip or bury it. */}
      <QuitConfirm
        open={!!pending}
        title="Leave this exercise?"
        message="You have not finished this session. Your answers from it will be lost, and it will not count toward your streak."
        confirmLabel="Leave anyway"
        cancelLabel="Keep going"
        onConfirm={confirmLeave}
        onCancel={() => setPending(null)}
      />
    </aside>
  );
}
